import React from "react";

type ParticleBasketballQuality = "hero" | "compact";

interface ParticleBasketballProps {
  className?: string;
  height?: number | string;
  quality?: ParticleBasketballQuality;
  width?: number | string;
}

type Point = {
  x: number;
  y: number;
  z: number;
};

const VERTEX_SHADER_SOURCE = `
  precision highp float;

  attribute vec3 aPosition;
  attribute float aSeed;
  attribute float aKind;

  uniform vec2 uResolution;
  uniform vec2 uPointer;
  uniform float uPointerStrength;
  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uPointMultiplier;
  uniform float uOpacity;
  uniform vec3 uBrandPrimary;
  uniform vec3 uBrandSecondary;
  uniform vec3 uBrandTertiary;

  varying vec3 vColor;
  varying float vAlpha;

  mat3 rotateX(float angle) {
    float sine = sin(angle);
    float cosine = cos(angle);
    return mat3(
      1.0, 0.0, 0.0,
      0.0, cosine, -sine,
      0.0, sine, cosine
    );
  }

  mat3 rotateY(float angle) {
    float sine = sin(angle);
    float cosine = cos(angle);
    return mat3(
      cosine, 0.0, sine,
      0.0, 1.0, 0.0,
      -sine, 0.0, cosine
    );
  }

  mat3 rotateZ(float angle) {
    float sine = sin(angle);
    float cosine = cos(angle);
    return mat3(
      cosine, -sine, 0.0,
      sine, cosine, 0.0,
      0.0, 0.0, 1.0
    );
  }

  void main() {
    float isSeam = step(0.5, aKind) * (1.0 - step(1.5, aKind));
    float isDust = step(1.5, aKind);
    vec3 position = aPosition;

    if (isDust > 0.5) {
      float drift = sin(uTime * 0.62 + aSeed * 47.0) * 0.025;
      position *= 1.0 + drift;
    }

    float yaw = 0.52 + uTime * 0.13 + uPointer.x * 0.2;
    float pitch = -0.24 + sin(uTime * 0.34) * 0.035 - uPointer.y * 0.12;
    float roll = -0.13;
    vec3 rotated = rotateZ(roll) * rotateX(pitch) * rotateY(yaw) * position;

    float front = smoothstep(-1.05, 0.68, rotated.z);
    float perspective = 1.0 + rotated.z * 0.045;
    vec2 projected = rotated.xy * 0.84 * perspective;
    projected.x += uPointer.x * (0.018 + front * 0.012);
    projected.y -= uPointer.y * (0.012 + front * 0.008);
    projected.y += sin(uTime * 0.58) * 0.022;

    float aspectCorrection = uResolution.y / uResolution.x;
    vec2 pointerPosition = vec2(
      uPointer.x / aspectCorrection,
      -uPointer.y
    );
    vec2 pointerDelta = projected - pointerPosition;
    float pointerDistance = length(pointerDelta);
    float particleNoise =
      fract(sin(aSeed * 91.345 + 0.137) * 47453.5453);
    float chaosPhase =
      aSeed * 118.37 + uTime * (1.8 + particleNoise * 2.6);
    float irregularRadius =
      0.25 +
      particleNoise * 0.14 +
      sin(chaosPhase * 0.47) * 0.025;
    float pointerField =
      1.0 - smoothstep(0.015, irregularRadius, pointerDistance);
    pointerField *= uPointerStrength;
    vec2 radialDirection = pointerDelta / max(pointerDistance, 0.001);
    vec2 tangentDirection = vec2(-radialDirection.y, radialDirection.x);
    float chaosAngle =
      (particleNoise - 0.5) * 4.2 +
      sin(chaosPhase) * 0.9 +
      sin(chaosPhase * 0.43) * 0.45;
    vec2 scatterDirection = vec2(cos(chaosAngle), sin(chaosAngle));
    float burstPulse = 0.55 + sin(chaosPhase * 1.7) * 0.45;
    float participation = smoothstep(
      0.26,
      0.82,
      fract(particleNoise + aSeed * 7.13)
    );
    vec2 explosionDirection = normalize(
      radialDirection * (0.22 + particleNoise * 0.28) +
      tangentDirection * sin(chaosPhase * 0.81) * 0.35 +
      scatterDirection * (0.7 + burstPulse * 0.4)
    );
    float explosionStrength =
      pointerField *
      participation *
      (0.08 + particleNoise * 0.3) *
      (0.65 + burstPulse * 0.55);
    projected += explosionDirection * explosionStrength;

    gl_Position = vec4(
      projected.x * aspectCorrection,
      projected.y,
      0.0,
      1.0
    );

    vec3 normal = normalize(rotated);
    vec3 lightDirection = normalize(vec3(-0.48, 0.72, 1.0));
    float diffuse = max(dot(normal, lightDirection), 0.0);
    float rim = pow(1.0 - abs(normal.z), 2.0);

    float gradientPosition = smoothstep(-0.82, 0.78, rotated.y);
    vec3 surfaceColor = mix(
      uBrandSecondary,
      uBrandPrimary,
      gradientPosition
    );
    float cyanLight = smoothstep(
      0.12,
      1.0,
      dot(normal, normalize(vec3(-0.8, 0.45, 0.9)))
    );
    surfaceColor = mix(
      surfaceColor,
      uBrandTertiary,
      cyanLight * 0.68
    );
    surfaceColor *= 0.38 + diffuse * 0.95 + rim * 0.24;

    vec3 seamColor = mix(
      uBrandTertiary,
      vec3(0.82, 0.9, 1.0),
      0.26 + diffuse * 0.42
    );
    vec3 dustColor = mix(
      uBrandPrimary,
      uBrandSecondary,
      aSeed
    );
    dustColor = mix(dustColor, uBrandTertiary, diffuse * 0.48);

    vColor = mix(surfaceColor, seamColor, isSeam);
    vColor = mix(vColor, dustColor, isDust);

    float shimmer = 0.76 + sin(aSeed * 83.0 + uTime * 1.7) * 0.24;
    float surfaceAlpha = mix(0.16, 0.9, front) * shimmer;
    float seamAlpha = mix(0.34, 1.0, front) * shimmer;
    float dustAlpha = mix(0.05, 0.22, front) * shimmer;
    vAlpha = mix(surfaceAlpha, seamAlpha, isSeam);
    vAlpha = mix(vAlpha, dustAlpha, isDust) * uOpacity;

    float pointSize = mix(1.25, 1.85, isSeam);
    pointSize = mix(pointSize, 0.9, isDust);
    pointSize *= mix(0.72, 1.28, smoothstep(-1.0, 1.0, rotated.z));
    gl_PointSize = pointSize * uPixelRatio * uPointMultiplier;
  }
`;

const FRAGMENT_SHADER_SOURCE = `
  precision mediump float;

  uniform float uSoftPass;

  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    float distanceFromCenter = length(gl_PointCoord - vec2(0.5)) * 2.0;
    if (distanceFromCenter > 1.0) {
      discard;
    }

    float core = smoothstep(1.0, 0.08, distanceFromCenter);
    float glow = exp(-3.25 * distanceFromCenter * distanceFromCenter);
    float particleAlpha = mix(core * core, glow, uSoftPass) * vAlpha;
    gl_FragColor = vec4(vColor, particleAlpha);
  }
`;

function createRandom(seed: number) {
  let value = seed >>> 0;
  return function random() {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function normalizePoint(x: number, y: number, z: number): Point {
  const length = Math.sqrt(x * x + y * y + z * z);
  return {
    x: x / length,
    y: y / length,
    z: z / length,
  };
}

function createPointCloud(
  quality: ParticleBasketballQuality,
  renderedHeight: number
) {
  const values: number[] = [];
  const random = createRandom(0x0bd1ba11);
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const compactViewport = window.innerWidth <= 768;
  const sizeScale = Math.max(0.16, Math.min(1, renderedHeight / 380));
  const areaScale = sizeScale * sizeScale;
  const surfaceSampleCount =
    quality === "compact"
      ? Math.max(2600, Math.round(64000 * areaScale))
      : compactViewport
      ? 26000
      : 64000;
  const curvedSeamPointCount =
    quality === "compact"
      ? Math.max(560, Math.round(2900 * sizeScale))
      : compactViewport
      ? 1500
      : 2900;
  const greatCirclePointCount =
    quality === "compact"
      ? Math.max(340, Math.round(1750 * sizeScale))
      : compactViewport
      ? 950
      : 1750;
  const dustPointCount =
    quality === "compact"
      ? Math.max(140, Math.round(3200 * areaScale))
      : compactViewport
      ? 1200
      : 3200;
  const curvedSeam: Point[] = [];

  function addPoint(x: number, y: number, z: number, kind: number) {
    values.push(x, y, z, random(), kind);
  }

  // Traditional eight-panel geometry: two perpendicular great circles and
  // one continuous four-lobed curve generated from an Enneper surface.
  const enneperRadius = 1.08;
  for (let index = 0; index < curvedSeamPointCount; index++) {
    const angle = (index / curvedSeamPointCount) * Math.PI * 2;
    const u = enneperRadius * Math.cos(angle);
    const v = enneperRadius * Math.sin(angle);
    curvedSeam.push(
      normalizePoint(
        u - (u * u * u) / 3 + u * v * v,
        v - (v * v * v) / 3 + v * u * u,
        u * u - v * v
      )
    );
  }

  const curveCellSize = 0.07;
  const curveGridWidth = 64;
  const curveBuckets = new Map<number, Point[]>();

  function curveCell(value: number) {
    return Math.floor((value + 1.1) / curveCellSize);
  }

  function curveCellKey(x: number, y: number, z: number) {
    return x + y * curveGridWidth + z * curveGridWidth * curveGridWidth;
  }

  curvedSeam.forEach((point) => {
    const key = curveCellKey(
      curveCell(point.x),
      curveCell(point.y),
      curveCell(point.z)
    );
    const bucket = curveBuckets.get(key);
    if (bucket) {
      bucket.push(point);
    } else {
      curveBuckets.set(key, [point]);
    }
  });

  function isNearCurvedSeam(x: number, y: number, z: number) {
    const cellX = curveCell(x);
    const cellY = curveCell(y);
    const cellZ = curveCell(z);
    const thresholdSquared = 0.037 * 0.037;

    for (let offsetX = -1; offsetX <= 1; offsetX++) {
      for (let offsetY = -1; offsetY <= 1; offsetY++) {
        for (let offsetZ = -1; offsetZ <= 1; offsetZ++) {
          const bucket = curveBuckets.get(
            curveCellKey(
              cellX + offsetX,
              cellY + offsetY,
              cellZ + offsetZ
            )
          );
          if (!bucket) continue;

          for (const point of bucket) {
            const deltaX = x - point.x;
            const deltaY = y - point.y;
            const deltaZ = z - point.z;
            if (
              deltaX * deltaX + deltaY * deltaY + deltaZ * deltaZ <
              thresholdSquared
            ) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  for (let index = 0; index < surfaceSampleCount; index++) {
    const baseY = 1 - (2 * (index + 0.5)) / surfaceSampleCount;
    const y = Math.max(
      -1,
      Math.min(1, baseY + (random() - 0.5) * 0.004)
    );
    const radial = Math.sqrt(Math.max(0, 1 - y * y));
    const angle = goldenAngle * index + (random() - 0.5) * 0.022;
    const radius = 1 + (random() - 0.5) * 0.011;
    const x = Math.cos(angle) * radial;
    const z = Math.sin(angle) * radial;
    const greatCircleDistance = Math.min(Math.abs(x), Math.abs(y));

    if (
      greatCircleDistance > 0.031 &&
      !isNearCurvedSeam(x, y, z)
    ) {
      addPoint(x * radius, y * radius, z * radius, 0);
    }
  }

  for (let circle = 0; circle < 2; circle++) {
    for (let index = 0; index < greatCirclePointCount; index++) {
      const angle =
        (index / greatCirclePointCount) * Math.PI * 2 +
        (random() - 0.5) * 0.004;
      const planeOffset = (random() - 0.5) * 0.016;
      let x: number;
      let y: number;
      let z: number;

      if (circle === 0) {
        x = Math.cos(angle);
        y = planeOffset;
        z = Math.sin(angle);
      } else {
        x = planeOffset;
        y = Math.cos(angle);
        z = Math.sin(angle);
      }

      const point = normalizePoint(x, y, z);
      const radius = 1.012 + (random() - 0.5) * 0.009;
      addPoint(
        point.x * radius,
        point.y * radius,
        point.z * radius,
        1
      );
    }
  }

  curvedSeam.forEach((point) => {
    const jitteredPoint = normalizePoint(
      point.x + (random() - 0.5) * 0.009,
      point.y + (random() - 0.5) * 0.009,
      point.z + (random() - 0.5) * 0.009
    );
    const radius = 1.012 + (random() - 0.5) * 0.009;
    addPoint(
      jitteredPoint.x * radius,
      jitteredPoint.y * radius,
      jitteredPoint.z * radius,
      1
    );
  });

  for (let index = 0; index < dustPointCount; index++) {
    const y = random() * 2 - 1;
    const radial = Math.sqrt(Math.max(0, 1 - y * y));
    const angle = random() * Math.PI * 2;
    const radius = 1.07 + Math.pow(random(), 1.8) * 0.48;
    addPoint(
      Math.cos(angle) * radial * radius,
      y * radius,
      Math.sin(angle) * radial * radius,
      2
    );
  }

  return new Float32Array(values);
}

function initializeParticleBasketball(
  canvas: HTMLCanvasElement,
  container: HTMLDivElement,
  quality: ParticleBasketballQuality
) {
  const context = canvas.getContext("webgl", {
    alpha: true,
    antialias: true,
    depth: false,
    premultipliedAlpha: false,
    powerPreference: "high-performance",
  });

  if (!context) return () => undefined;
  const gl = context;

  function createShader(type: number, source: string) {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("Unable to create particle shader");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Unable to compile particle shader: ${message}`);
    }

    return shader;
  }

  function createProgram() {
    const vertexShader = createShader(
      gl.VERTEX_SHADER,
      VERTEX_SHADER_SOURCE
    );
    const fragmentShader = createShader(
      gl.FRAGMENT_SHADER,
      FRAGMENT_SHADER_SOURCE
    );
    const nextProgram = gl.createProgram();
    if (!nextProgram) throw new Error("Unable to create particle program");

    gl.attachShader(nextProgram, vertexShader);
    gl.attachShader(nextProgram, fragmentShader);
    gl.linkProgram(nextProgram);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    if (!gl.getProgramParameter(nextProgram, gl.LINK_STATUS)) {
      const message = gl.getProgramInfoLog(nextProgram);
      gl.deleteProgram(nextProgram);
      throw new Error(`Unable to link particle shaders: ${message}`);
    }

    return nextProgram;
  }

  let program: WebGLProgram;
  try {
    program = createProgram();
  } catch (error) {
    console.warn(error);
    return () => undefined;
  }

  const renderedHeight = Math.max(
    1,
    container.getBoundingClientRect().height
  );
  const pointCloud = createPointCloud(quality, renderedHeight);
  const pointCount = pointCloud.length / 5;
  const buffer = gl.createBuffer();
  if (!buffer) {
    gl.deleteProgram(program);
    return () => undefined;
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, pointCloud, gl.STATIC_DRAW);
  gl.useProgram(program);

  const stride = 5 * Float32Array.BYTES_PER_ELEMENT;
  const positionLocation = gl.getAttribLocation(program, "aPosition");
  const seedLocation = gl.getAttribLocation(program, "aSeed");
  const kindLocation = gl.getAttribLocation(program, "aKind");

  gl.enableVertexAttribArray(positionLocation);
  gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, stride, 0);
  gl.enableVertexAttribArray(seedLocation);
  gl.vertexAttribPointer(
    seedLocation,
    1,
    gl.FLOAT,
    false,
    stride,
    3 * Float32Array.BYTES_PER_ELEMENT
  );
  gl.enableVertexAttribArray(kindLocation);
  gl.vertexAttribPointer(
    kindLocation,
    1,
    gl.FLOAT,
    false,
    stride,
    4 * Float32Array.BYTES_PER_ELEMENT
  );

  const uniforms = {
    resolution: gl.getUniformLocation(program, "uResolution"),
    pointer: gl.getUniformLocation(program, "uPointer"),
    pointerStrength: gl.getUniformLocation(program, "uPointerStrength"),
    time: gl.getUniformLocation(program, "uTime"),
    pixelRatio: gl.getUniformLocation(program, "uPixelRatio"),
    pointMultiplier: gl.getUniformLocation(program, "uPointMultiplier"),
    opacity: gl.getUniformLocation(program, "uOpacity"),
    softPass: gl.getUniformLocation(program, "uSoftPass"),
    brandPrimary: gl.getUniformLocation(program, "uBrandPrimary"),
    brandSecondary: gl.getUniformLocation(program, "uBrandSecondary"),
    brandTertiary: gl.getUniformLocation(program, "uBrandTertiary"),
  };

  gl.uniform3f(uniforms.brandPrimary, 59 / 255, 130 / 255, 246 / 255);
  gl.uniform3f(uniforms.brandSecondary, 139 / 255, 92 / 255, 246 / 255);
  gl.uniform3f(uniforms.brandTertiary, 6 / 255, 182 / 255, 212 / 255);
  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ONE, gl.ONE);
  gl.disable(gl.DEPTH_TEST);
  gl.clearColor(0, 0, 0, 0);

  let pixelRatio = 1;
  function resizeCanvas() {
    const rect = canvas.getBoundingClientRect();
    pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(rect.width * pixelRatio));
    const height = Math.max(1, Math.round(rect.height * pixelRatio));

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
    }

    gl.uniform2f(uniforms.resolution, width, height);
    gl.uniform1f(uniforms.pixelRatio, pixelRatio);
  }

  const pointer = { x: 0, y: 0, strength: 0 };
  const pointerTarget = { x: 0, y: 0, strength: 0 };

  const onPointerMove = (event: PointerEvent) => {
    if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;
    const rect = container.getBoundingClientRect();
    pointerTarget.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointerTarget.y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    pointerTarget.strength = 1;
  };

  const onPointerLeave = () => {
    pointerTarget.x = 0;
    pointerTarget.y = 0;
    pointerTarget.strength = 0;
  };

  container.addEventListener("pointermove", onPointerMove);
  container.addEventListener("pointerleave", onPointerLeave);

  const motionPreference = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );
  const startTime = performance.now();
  let animationFrame: number | null = null;
  let isVisible = true;
  let disposed = false;

  function drawPass(
    pointMultiplier: number,
    opacity: number,
    softPass: number
  ) {
    gl.uniform1f(uniforms.pointMultiplier, pointMultiplier);
    gl.uniform1f(uniforms.opacity, opacity);
    gl.uniform1f(uniforms.softPass, softPass);
    gl.drawArrays(gl.POINTS, 0, pointCount);
  }

  function renderFrame(timestamp: number) {
    animationFrame = null;
    if (disposed) return;
    resizeCanvas();

    pointer.x += (pointerTarget.x - pointer.x) * 0.055;
    pointer.y += (pointerTarget.y - pointer.y) * 0.055;
    pointer.strength +=
      (pointerTarget.strength - pointer.strength) * 0.085;

    const elapsed = motionPreference.matches
      ? 4.5
      : (timestamp - startTime) / 1000;

    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform1f(uniforms.time, elapsed);
    gl.uniform2f(uniforms.pointer, pointer.x, pointer.y);
    gl.uniform1f(uniforms.pointerStrength, pointer.strength);
    drawPass(4.4, 0.095, 1);
    drawPass(1, 0.92, 0);

    if (!motionPreference.matches && isVisible && !document.hidden) {
      animationFrame = window.requestAnimationFrame(renderFrame);
    }
  }

  function requestRender() {
    if (
      !disposed &&
      animationFrame === null &&
      isVisible &&
      !document.hidden
    ) {
      animationFrame = window.requestAnimationFrame(renderFrame);
    }
  }

  function stopRendering() {
    if (animationFrame !== null) {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }
  }

  const resizeObserver = new ResizeObserver(requestRender);
  resizeObserver.observe(container);

  const visibilityObserver = new IntersectionObserver((entries) => {
    isVisible = entries[0].isIntersecting;
    if (isVisible) {
      requestRender();
    } else {
      stopRendering();
    }
  });
  visibilityObserver.observe(container);

  const onVisibilityChange = () => {
    if (document.hidden) {
      stopRendering();
    } else {
      requestRender();
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  motionPreference.addEventListener("change", requestRender);
  requestRender();

  return () => {
    disposed = true;
    stopRendering();
    resizeObserver.disconnect();
    visibilityObserver.disconnect();
    document.removeEventListener("visibilitychange", onVisibilityChange);
    motionPreference.removeEventListener("change", requestRender);
    container.removeEventListener("pointermove", onPointerMove);
    container.removeEventListener("pointerleave", onPointerLeave);
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
  };
}

export const ParticleBasketball: React.FC<ParticleBasketballProps> = ({
  className,
  height,
  quality = "hero",
  width,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    return initializeParticleBasketball(canvas, container, quality);
  }, [quality]);

  return (
    <div
      ref={containerRef}
      className={className}
      aria-hidden="true"
      data-particle-basketball
      style={{
        flex: "0 0 auto",
        height,
        position: "relative",
        width,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          filter:
            "saturate(1.12) drop-shadow(0 0 16px rgba(59, 130, 246, 0.16)) drop-shadow(0 0 30px rgba(139, 92, 246, 0.09))",
          height: "100%",
          pointerEvents: "none",
          userSelect: "none",
          width: "100%",
        }}
      />
    </div>
  );
};
